import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Button,
  CircularProgress,
  Grid,
  TextField,
  Typography
} from '@material-ui/core';

import {
  fetchExternalAPIKey,
  generateExternalAPIKey
} from '../../../../../../modules/factoring';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from '../../../../../../components/Dialog';
import copyText from '../../../../../../helpers/copyText';
import getErrorMessage from '../../../../../../helpers/getErrorMessage';

const REGENERATE_WARNING =
  "This issues a new key and invalidates the client's current key immediately. Any integration using the old key will stop working until it's updated.";

const API_KEY_ERROR = 'Unable to manage the external API key. Please try again.';

const useExternalApiKey = ({ factoringId, configuredTmsId, openSnackbar }) => {
  const dispatch = useDispatch();
  const [apiKey, setApiKey] = useState(null);
  const [isLoading, setLoading] = useState(true);
  const [isGenerating, setGenerating] = useState(false);
  const [isRevealed, setRevealed] = useState(false);
  const [isConfirmOpen, setConfirmOpen] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isActive = true;
    setLoading(true);
    setApiKey(null);
    setRevealed(false);
    setError(null);

    dispatch(fetchExternalAPIKey(factoringId))
      .then(response => {
        if (isActive) setApiKey(response.token || null);
      })
      .catch(err => {
        if (isActive) {
          const message = getErrorMessage(err, API_KEY_ERROR);
          setApiKey(null);
          setError(message);
          openSnackbar('error', message);
        }
      })
      .finally(() => {
        if (isActive) setLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [configuredTmsId, dispatch, factoringId, openSnackbar]);

  const generateKey = async isRegeneration => {
    setGenerating(true);
    setError(null);
    try {
      const response = await dispatch(generateExternalAPIKey(factoringId));
      setApiKey(response.token);
      setRevealed(true);
      openSnackbar(
        'success',
        isRegeneration ? 'New key generated' : 'API key generated'
      );
    } catch (err) {
      const message = getErrorMessage(err, API_KEY_ERROR);
      setError(message);
      openSnackbar('error', message);
    } finally {
      setGenerating(false);
      setConfirmOpen(false);
    }
  };

  const handleCopy = async () => {
    try {
      await copyText(apiKey);
      openSnackbar('success', 'Copied to clipboard');
    } catch (error) {
      openSnackbar(
        'error',
        error && error.message
          ? `Unable to copy the API key: ${error.message}`
          : 'Unable to copy the API key.'
      );
    }
  };

  return {
    apiKey,
    generateKey,
    handleCopy,
    isConfirmOpen,
    isGenerating,
    isLoading,
    isRevealed,
    error,
    setConfirmOpen,
    setRevealed
  };
};

export default ({ factoringId, configuredTmsId, openSnackbar }) => {
  const {
    apiKey,
    generateKey,
    handleCopy,
    isConfirmOpen,
    isGenerating,
    isLoading,
    isRevealed,
    error,
    setConfirmOpen,
    setRevealed
  } = useExternalApiKey({ factoringId, configuredTmsId, openSnackbar });

  return (
    <Grid container direction="column" spacing={8}>
      <Grid item>
        <Typography variant="caption" color="textSecondary">
          External API Key
        </Typography>
      </Grid>
      {error && (
        <Grid item>
          <Typography color="error" variant="caption">
            {error}
          </Typography>
        </Grid>
      )}
      {isLoading ? (
        <Grid item>
          <CircularProgress size={20} />
        </Grid>
      ) : apiKey ? (
        <React.Fragment>
          <Grid item>
            <TextField
              fullWidth
              value={isRevealed ? apiKey : '••••••••••••••••'}
              inputProps={{
                'aria-label': 'External API Key',
                readOnly: true,
                style: { fontFamily: 'monospace' },
              }}
            />
          </Grid>
          <Grid item container spacing={8} justify="flex-end">
            <Grid item>
              <Button
                type="button"
                size="small"
                onClick={() => setRevealed(!isRevealed)}
              >
                {isRevealed ? 'Hide' : 'Show'}
              </Button>
            </Grid>
            <Grid item>
              <Button type="button" size="small" onClick={handleCopy}>
                Copy
              </Button>
            </Grid>
            <Grid item>
              <Button
                type="button"
                size="small"
                color="secondary"
                onClick={() => setConfirmOpen(true)}
              >
                Regenerate
              </Button>
            </Grid>
          </Grid>
        </React.Fragment>
      ) : (
        <Grid item container justify="flex-end">
          <Button
            type="button"
            size="small"
            variant="contained"
            color="primary"
            disabled={isGenerating}
            onClick={() => generateKey(false)}
          >
            {isGenerating ? 'Generating…' : 'Generate'}
          </Button>
        </Grid>
      )}

      <Dialog
        open={isConfirmOpen}
        maxWidth="sm"
        fullWidth
        onClose={() => !isGenerating && setConfirmOpen(false)}
      >
        <DialogTitle>Regenerate API key?</DialogTitle>
        <DialogContent>
          <Typography>{REGENERATE_WARNING}</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            type="button"
            onClick={() => setConfirmOpen(false)}
            disabled={isGenerating}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="contained"
            onClick={() => generateKey(true)}
            disabled={isGenerating}
            style={{ backgroundColor: '#c62828', color: '#fff' }}
          >
            {isGenerating ? 'Regenerating…' : 'Regenerate key'}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};
